import { h, Component } from 'preact';
import Store from 'store/store';
import * as jsonPointer from 'utils/jsonPointer';
import { joinClasses } from 'utils/utils';

import InheritIndicator from 'components/editor/settings/inheritIndicator';
import { Sortable, SortableItem, Handle } from 'components/sortable/sortable';

import s from 'components/cardList/cardList.sss';

/**
 * Default item renderer for CardList.
 * Renders a sortable item with handle, label, state indicator, and edit button.
 *
 * Props:
 * @param {Object} item - The item data object
 * @param {number} index - The item index
 * @param {string} [label] - Display label (defaults to item.name)
 * @param {string} [stateLabel] - Optional state indicator (e.g. "(hidden)")
 * @param {Function} [onEdit] - Edit button click handler
 * @param {string} [editLabel] - Edit button text (default: "Edit")
 * @param {boolean} [disabled] - Whether the item is disabled/non-editable
 * @param {string} [class] - Additional CSS class
 */
export class CardListItem extends Component {
	s = s;

	renderLabel() {
		const { label, description } = this.props;

		return (
			<span>
				{label}
				{description && <span class={this.s.description}>{description}</span>}
			</span>
		);
	}

	renderInheritIndicator() {
		const { path, inheritGroup, inheritPath, inheritType } = this.props;
		if (!path || !inheritPath) return null;
		const inheritData = jsonPointer.get(Store.get().data, inheritPath);
		if (!inheritData) return null;

		return (
			<InheritIndicator path={path} type={inheritType} group={inheritGroup} dark hideReset />
		);
	}

	renderAction() {
		const {
			onEdit,
			editLabel = 'Edit',
			disabled = false,
		} = this.props;

		if (!onEdit) return null;

		return (
			<button class={this.s.editButton} disabled={disabled} onClick={onEdit}>
				{editLabel}
			</button>
		);
	}

	render() {
		return (
			<div class={this.s.item}>
				{this.renderLabel()}
				{this.renderInheritIndicator()}
				{this.renderAction()}
			</div>
		);
	}
}

/**
 * A generic sortable card list component.
 *
 * Props:
 * @prop {Array} items - Array of item data objects
 * @prop {Function} onChange - Callback when the order of items changes
 * @prop {boolean} [disabled=false] - Whether the list is disabled/non-editable
 * @prop {Function} [renderItem] - Custom item renderer function (item, index, props) => JSX
 * @prop {Function} [getItemProps] - Function to get item props (item) => { label, description, onEdit, ... }
 * @prop {string} [class] - Additional CSS class for the list container
 */
export class CardList extends Component {
	renderListItem(item, index, itemProps) {
		const { renderItem } = this.props;
		const { class: className, disabled, ...extraProps } = itemProps;
		const renderedItem = renderItem ? renderItem(item, index, itemProps) : <CardListItem key={item?.id || index} {...extraProps} />;
		
		return (
			<SortableItem key={item?.id || index} {...extraProps} class={joinClasses(s.itemWrapper, disabled && s.disabled, className)} disabled={disabled}>
				{!disabled && <Handle class={s.handle} />}
				{renderedItem}
			</SortableItem>
		);
	}

	render() {
		const {
			children,
			items,
			onChange,
			disabled = false,
			getItemProps = (item) => ({ label: item.name }),
			class: className,
		} = this.props;

		return (
			<Sortable class={joinClasses(s.list, className)} onChange={onChange} disabled={disabled}>
				{children}
				{items.map((item, i) => this.renderListItem(item, i, getItemProps(item, i)))}
			</Sortable>
		);
	}
}

export default CardList;
